/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import RequireForAccess from 'react-access';

import {
  createNewAccount,
  updateRegion,
  addAccountNo,
  addAssetRequestDetails,
} from 'permissions/account';

import { manageCaaSPermissions } from 'permissions/caas';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import AutoCompleteData from 'models/forms/AutoCompleteModel.js';
import { autoCompleteRegions } from 'api/forms.js';

import ElectroCheckbox from '../components/inputs/ElectroCheckbox/ElectroCheckbox';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';

import FormControls from '../components/controls/FormControls/FormControls';

import { makeFields, accountCreate, accountEdit } from './setupAccountForm';

type TypeProps = {|
  mode: string,
  accountData: Object,
  accountStore: Object,
  userStore: Object,
|} & TranslatorProps;

@translate()
@inject('accountStore', 'userStore')
@observer
export default class AccountForm extends React.Component<*> {
  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  props: TypeProps;

  @computed get form(): Object {
    const accountData: Object =
      typeof this.props.accountData === 'undefined' ? {} : this.props.accountData;
    const fields: Array<*> = makeFields(accountData, this.props.t);

    return this.props.mode === 'edit'
      ? accountEdit(fields, accountData, this.props.accountStore, this.localState, this.props.t)
      : accountCreate(fields, this.localState, this.props.t);
  }

  /**
   * Reset CaaS text fields to an empty value.
   */
  resetCaaSTextFields() {
    this.form.$('accountPreferences.erp_order_id').clear();
    this.form.$('accountPreferences.caas_customer_po').clear();
  }

  /**
   * Reset CaaS checkboxes to an unchecked state.
   */
  resetCaaSCheckboxes() {
    this.form.$('accountPreferences.is_caas_active').set(false);
    this.form.$('accountPreferences.is_caas_customer').set(false);
  }

  /**
   * Reset all CaaS fields to its default value.
   */
  resetAllCaaSFields() {
    this.resetCaaSCheckboxes();
    this.resetCaaSTextFields();
  }

  /**
   * Clear CaaS values when the region changes and it is not AMERICAS.
   */
  handleRegionOnChange = region => {
    if (region.name !== 'AMERICAS') {
      this.resetAllCaaSFields();
    }
  };

  /**
   * Handles the change event for the "Allow Calibartion as a Service" checkbox.
   */
  handleIsCaaSCustomerOnChange = checkbox => {
    // Reset CaaS values if "Allow CaaS" is unckecked.
    if (checkbox.value === false) {
      this.resetAllCaaSFields();
    }
  };

  /**
   * Handles the change event for the "CaaS Account Active" checkbox.
   */
  handleIsCaaSActiveOnChange = checkbox => {
    // Reset CaaS text fields if "CaaS Account Active" is unchecked
    if (checkbox.value === false) {
      this.resetCaaSTextFields();
    }
  };

  render(): React.Element<*> {
    const regionsModel: AutoCompleteData = new AutoCompleteData(autoCompleteRegions);
    const t = this.props.t;
    const isCaaSActive = this.form.$('accountPreferences.is_caas_active').value;
    const isCaaSCustomer = this.form.$('accountPreferences.is_caas_customer').value;
    const isAmericasRegion = this.form.$('accountRegion.region_id').value === 'AMERICAS';

    return (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <FormWrapper form={this.form}>
          <FormSection form={this.form} section="accountDetail">
            <form>
              <ElectroTextField field={this.form.$('accountDetail.accountName')} />
              <RequireForAccess
                permissions={addAccountNo}
                invalidAccessComponent={
                  <ElectroTextField
                    disabled={true}
                    field={this.form.$('accountDetail.accountNumber')}
                  />
                }
              >
                <ElectroTextField field={this.form.$('accountDetail.accountNumber')} />
              </RequireForAccess>
              <RequireForAccess
                permissions={addAssetRequestDetails}
                invalidAccessComponent={
                  <ElectroTextField
                    disabled={true}
                    field={this.form.$('accountDetail.accountPrefix')}
                  />
                }
              >
                <ElectroTextField field={this.form.$('accountDetail.accountPrefix')} />
              </RequireForAccess>
            </form>
          </FormSection>
          <RequireForAccess permissions={updateRegion}>
            <FormSection form={this.form} section="accountRegion">
              <form>
                <ElectroSelectExtra
                  field={this.form.$('accountRegion.region_id')}
                  form={this.form}
                  formName="accountRegion"
                  labelKey="name"
                  props={{
                    model: regionsModel,
                  }}
                  handleOnChange={this.handleRegionOnChange}
                />
              </form>
            </FormSection>
          </RequireForAccess>
          <RequireForAccess permissions={createNewAccount}>
            <FormSection form={this.form} section="accountPreferences">
              <form>
                <ElectroCheckbox field={this.form.$('accountPreferences.saleConsignment')} />
                <br />
                <ElectroCheckbox field={this.form.$('accountPreferences.subrentalProgram')} />
                <br />
                <RequireForAccess permissions={addAssetRequestDetails}>
                  <ElectroCheckbox field={this.form.$('accountPreferences.newAssetRequest')} />
                  <ElectroTextField
                    disabled={!this.form.$('accountPreferences.newAssetRequest').value}
                    field={this.form.$('accountPreferences.newAssetRequestEmail')}
                  />
                  {isAmericasRegion && (
                    <>
                      <ElectroCheckbox
                        field={this.form.$('accountPreferences.newRushAssetRequest')}
                      />
                      <ElectroTextField
                        disabled={!this.form.$('accountPreferences.newRushAssetRequest').value}
                        field={this.form.$('accountPreferences.newRushAssetRequestEmail')}
                      />
                    </>
                  )}
                  <ElectroCheckbox field={this.form.$('accountPreferences.newLicenseRequest')} />
                  <ElectroTextField
                    disabled={!this.form.$('accountPreferences.newLicenseRequest').value}
                    field={this.form.$('accountPreferences.newLicenseRequestEmail')}
                  />
                </RequireForAccess>
                {isAmericasRegion && (
                  <RequireForAccess permissions={manageCaaSPermissions}>
                    <ElectroCheckbox
                      onChange={this.handleIsCaaSCustomerOnChange}
                      field={this.form.$('accountPreferences.is_caas_customer')}
                    />
                    <br />
                    <ElectroCheckbox
                      disabled={!isCaaSCustomer}
                      onChange={this.handleIsCaaSActiveOnChange}
                      field={this.form.$('accountPreferences.is_caas_active')}
                    />
                    <br />
                    <ElectroTextField
                      disabled={!isCaaSCustomer || !isCaaSActive}
                      field={this.form.$('accountPreferences.caas_customer_po')}
                    />
                    <ElectroTextField
                      disabled={!isCaaSCustomer || !isCaaSActive}
                      field={this.form.$('accountPreferences.erp_order_id')}
                    />
                  </RequireForAccess>
                )}
              </form>
            </FormSection>
          </RequireForAccess>
          <FormSection form={this.form} section="assetCustomFields">
            <form>
              <ElectroTextField field={this.form.$('assetCustomFields.customLabel1')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customHelp1')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customLabel2')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customHelp2')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customLabel3')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customHelp3')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customLabel4')} />
              <ElectroTextField field={this.form.$('assetCustomFields.customHelp4')} />
            </form>
          </FormSection>
          <FormControls
            form={this.form}
            buttonText={
              this.props.mode === 'edit' ? t('general:actions.save') : t('general:actions.create')
            }
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}
